import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Layout from './components/Layout'
import ScrollToTop from './components/ScrollToTop'
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Signup from './pages/Signup';
import OAuth2RedirectHandler from './pages/OAuth2RedirectHandler'; // Added
import QR from './pages/QR';
import Menu from './pages/Menu';
import OrdersWithRealtimeUpdates from './pages/OrdersWithRealtimeUpdates';
import OrderDetail from './pages/OrderDetail';
import VendorOrders from './pages/VendorOrders';
import VendorOrderDetail from './pages/VendorOrderDetail';
// VendorLogin removed - using unified Login page
import VendorOrderLines from './pages/VendorOrderLines';
import VendorTimeline from './pages/VendorTimeline';
// StewardLogin removed - using unified Login page
// StewardDashboard removed - not used in current routing
import StewardNotificationPage from './pages/StewardNotificationPage';
import StewardAccount from './pages/StewardAccount';
import StewardManagement from './pages/StewardManagement';
import UserManagement from './pages/UserManagement';
import AdminOrders from './pages/AdminOrders';
import AdminVendorMenu from './pages/AdminVendorMenu';
import AdminVendorOnboarding from './pages/AdminVendorOnboarding';
import AdminMenuManagement from './pages/AdminMenuManagement';
import AdminPortionTemplates from './pages/AdminPortionTemplates';
import VendorPageAccess from './pages/VendorPageAccess';
import './utils/authDiagnostics'; // Import auth diagnostics
import './utils/fixGrandhotelRoles'; // Import grandhotel role fix
import CustomerInvoices from './pages/CustomerInvoices';
import InvoiceDetail from './pages/InvoiceDetail';
import CustomerOrderDetailsPage from './pages/CustomerOrderDetailsPage';
import Profile from './pages/Profile';

// Initialize React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});
import VendorInvoices from './pages/VendorInvoices';
import VendorReports from './pages/VendorReports';
import ApiPerformanceDashboard from './pages/ApiPerformanceDashboard';
import Checkout from './pages/Checkout';
import OrderSuccess from './pages/OrderSuccess';
import { useAuth } from './context/AuthContext';
// NotificationProvider removed - not used in current routing
import Scanner from './pages/Scanner';
import MenuWrapper from './components/MenuWrapper';
import TableLayoutPage from './pages/TableLayoutPage';
import StewardTableManagement from './pages/StewardTableManagement';
import Visual2DLayoutManager from './pages/Visual2DLayoutManager';
import KitchenDisplayPage from './pages/KitchenDisplayPage';
import ManageLayoutPage from './pages/ManageLayoutPage';
import GuestOrderTracking from './pages/GuestOrderTracking';
import CacheClearPrompt from './components/CacheClearPrompt';
import VendorSettings from './pages/VendorSettings';
import VendorDashboard from './pages/VendorDashboard';
import VendorConfigurationPage from './pages/VendorConfigurationPage';
import VendorTaxConfiguration from './pages/VendorTaxConfiguration';
import PrinterManagement from './pages/PrinterManagement';
import KitchenManagement from './pages/KitchenManagement';
import KOTDisplay from './pages/KOTDisplay';
import OrderStatusDisplay from './pages/OrderStatusDisplay';
import MultiMenuManagement from './pages/MultiMenuManagement';
import VisualKitchenMapPage from './pages/VisualKitchenMap';
import ButtonShowcase from './pages/ButtonShowcase';
import VendorTableOrdersView from './pages/VendorTableOrdersView';
import QROrderHandler from './components/QROrderHandler';
import { DiagnosticsPage } from './pages/DiagnosticsPage';
import MessagingDiagnostics from './pages/MessagingDiagnostics';
import VendorStewardSettings from './pages/VendorStewardSettings';
import StewardAttendancePage from './pages/StewardAttendancePage';
import VendorStewardReportPage from './pages/VendorStewardReportPage';
import SocketDiagnostics from './pages/SocketDiagnostics';
import VendorLoyaltyPrograms from './pages/VendorLoyaltyPrograms';
import VendorTips from './pages/VendorTips';
import CustomerRewards from './pages/CustomerRewards';
import CustomerDashboard from './pages/CustomerDashboard';
import ReservationDashboard from './pages/ReservationDashboard';
import ReservationSettings from './pages/ReservationSettings';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import SessionValidator from './components/SessionValidator';
import VendorFeatureRoute from './components/VendorFeatureRoute';
import VendorPublicPage from './pages/VendorPublicPage';
import ChannelChoicePage from './pages/ChannelChoicePage';
import WhatsAppSimulator from './pages/WhatsAppSimulator';
import InventoryManagement from './pages/InventoryManagement';
import FinanceDashboard from './pages/finance/FinanceDashboard';
import ExpenseManagement from './pages/finance/ExpenseManagement';
import IncomeManagement from './pages/finance/IncomeManagement';
import CashRegister from './pages/finance/CashRegister';
import SupplierPayables from './pages/finance/SupplierPayables';
import FinanceSettings from './pages/finance/FinanceSettings';
import BankImport from './pages/finance/BankImport';
import EodSummary from './pages/finance/EodSummary';
import EodHistory from './pages/finance/EodHistory';
import AggregatorsPage from './pages/finance/AggregatorsPage';
import StaffPayments from './pages/finance/StaffPayments';
import SettlementDetailPage from './pages/finance/SettlementDetailPage';
import StewardFinanceHome from './pages/steward/StewardFinanceHome';
import MarketingStudioPage from './pages/marketing-studio/MarketingStudioPage';
import CustomerDisplay from './pages/CustomerDisplay';
import EdgeCashPage from './pages/edge/EdgeCashPage';
import { clearVendorScopedCurrency } from './config/currency';

// Finance pages a finance-only steward may reach even though the route is
// otherwise gated to ROLE_VENDOR. Mirrors the allowlist in Layout.tsx.
const FINANCE_ONLY_STEWARD_ALLOWED = [
  '/vendor/finance/income',
  '/vendor/finance/expenses',
  '/vendor/finance/eod-summary',
  '/vendor/finance/eod-history',
];

function PrivateRoute({ children, roles }: { children: JSX.Element; roles?: string[] }) {
  const { token, user } = useAuth()
  const location = useLocation()
  const localStorageToken = localStorage.getItem('token');
  const hasToken = token || localStorageToken;
  if (!hasToken) return <Navigate to="/login" replace />

  // Authenticated dashboard pages always use the LOGIN-scope currency. If this
  // tab visited a guest QR menu (another restaurant, maybe another country),
  // that set a per-tab vendor-scope override which would otherwise leak into
  // the dashboard — e.g. an IN vendor's screens rendering £ after peeking at a
  // GB menu. Clear it synchronously so children render with the right symbol
  // on first paint (the CURRENCY proxy is not reactive). Idempotent.
  clearVendorScopedCurrency()

  if (roles && roles.length > 0) {
    const userRoles = user?.roles ?? [];
    const allowed = userRoles.some(r => roles.includes(r));
    if (!allowed) {
      // Finance-only stewards lack ROLE_VENDOR but are explicitly granted the
      // four finance pages. Without this bypass they bounce between "/" and
      // "/vendor/finance/income" via <Navigate replace>, hammering
      // history.replaceState() until Safari throws a SecurityError.
      const isFinanceOnlySteward = Boolean(user?.financeOnlySteward);
      const pathAllowed = FINANCE_ONLY_STEWARD_ALLOWED.some(p => location.pathname.startsWith(p));
      if (isFinanceOnlySteward && pathAllowed) return children
      return <Navigate to="/" replace />
    }
  }
  return children
}

function GuestOrAuthRoute({ children }: { children: JSX.Element }) {
  const { token, isGuest } = useAuth()
  return (token || isGuest) ? children : <Navigate to="/login" replace />
}

/**
 * Guards the /steward/finance route so it is only reachable by an
 * authenticated user whose JwtResponse carried `financeOnlySteward: true`.
 * Other users are bounced back to the role-based home redirect, which keeps
 * vendors / regular stewards on their normal landing page.
 */
function FinanceOnlyStewardRoute({ children }: { children: JSX.Element }) {
  const { token, user } = useAuth()
  if (!token || !user) return <Navigate to="/login" replace />
  if (!user.financeOnlySteward) return <Navigate to="/" replace />
  return children
}

// Role-based redirect for home page - sends users to their appropriate dashboard
function RoleBasedRedirect() {
  const { token, user } = useAuth()

  // If not logged in, go to /qr (public menu access)
  if (!token || !user) {
    return <Navigate to="/qr" replace />
  }

  // Finance-only stewards land on Income (one of the three allowed pages);
  // bottom nav + FinanceTabBar self-filter to Income / Expenses / EOD only.
  if (user.financeOnlySteward) {
    return <Navigate to="/vendor/finance/income" replace />
  }

  // Per-user landing override (e.g. ranmoor bawarchi → /vendor/finance)
  if (user.defaultLandingPath) {
    return <Navigate to={user.defaultLandingPath} replace />
  }

  // Redirect based on role
  if (user.roles.includes('ROLE_ADMIN')) {
    return <Navigate to="/admin/orders" replace />
  }
  if (user.roles.includes('ROLE_VENDOR')) {
    return <Navigate to="/vendor/orders" replace />
  }
  if (user.roles.includes('ROLE_STEWARD')) {
    return <Navigate to="/steward/tables" replace />
  }

  // Default for regular users
  return <Navigate to="/qr" replace />
}

// Dark-designed display screens (KDS / KOT / status boards) manage their own
// palette; pin them to the LIGHT token context so the app-level dark mode's
// inverted neutral ramp cannot flip their panels. display:contents carries the
// data-theme attribute without adding a layout box.
const LightPinned = ({ children }: { children: React.ReactNode }) => (
  <div data-theme="light" style={{ display: 'contents' }}>{children}</div>
);

const App = () => {
  const location = useLocation();

  // Edge dual-screen till: the customer-facing screen is a local, physically
  // secured display — fullscreen, no Layout chrome, no auth guard. Rendered
  // outside the Layout/SessionValidator tree entirely.
  if (location.pathname === '/customer-display') {
    return <CustomerDisplay />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <SessionValidator>
        <Layout>
        <ScrollToTop />
        <CacheClearPrompt />
        <PWAInstallPrompt />
        <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/vendor/login" element={<Navigate to="/login" replace />} /> {/* Redirect to unified login */}
        <Route path="/steward/login" element={<Navigate to="/login" replace />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/oauth2/redirect" element={<OAuth2RedirectHandler />} />
        <Route path="/social-login-success" element={<OAuth2RedirectHandler />} /> {/* Added */}
        {/* Public routes - no authentication required */}
        <Route path="/qr" element={<QR />} />
        <Route path="/scan" element={<Scanner />} />
        <Route path="/menu" element={<Menu key="menu" />} />
        <Route path="/channel-choice" element={<ChannelChoicePage />} /> {/* WhatsApp vs Browser ordering choice */}
        <Route path="/v/:vendorId" element={<VendorPublicPage />} /> {/* Public vendor landing page */}
        <Route path="/menu/:vendorId" element={<MenuWrapper />} />
        <Route path="/track-order" element={<GuestOrderTracking />} />
        <Route path="/button-showcase" element={<ButtonShowcase />} />
        
        {/* QR Code Order Handler - Public route for table QR codes */}
        <Route path="/order/vendor/:vendorId/table/:tableId" element={<QROrderHandler />} />
        {/* Checkout handles auth internally */}
        <Route path="/checkout" element={<Checkout />} />
        {/* Order success is public - orderId acts as security token for anonymous/guest orders */}
        <Route path="/order-success/:orderId" element={<OrderSuccess />} />
        {/* Order detail - handles auth internally (public for anonymous, requires login for authenticated orders) */}
        <Route path="/orders/:orderId" element={<OrderDetail />} />
        {/* Private routes - require full authentication */}
        <Route path="/orders" element={<PrivateRoute><OrdersWithRealtimeUpdates /></PrivateRoute>} />
        <Route path="/vendor/dashboard" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorDashboard /></PrivateRoute>} />
        <Route path="/vendor/orders" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorOrders /></PrivateRoute>} />
        <Route path="/vendor/orders/:orderId" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorOrderDetail /></PrivateRoute>} />
        <Route path="/vendor/order-lines" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorFeatureRoute feature="orderLines"><VendorOrderLines /></VendorFeatureRoute></PrivateRoute>} />
        <Route path="/vendor/timeline" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorTimeline /></PrivateRoute>} />
        <Route path="/vendor/tables" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorFeatureRoute feature="tableManagement"><Visual2DLayoutManager /></VendorFeatureRoute></PrivateRoute>} /> {/* Visual 2D Table Layout - Same as steward view */}
        <Route path="/vendor/tables/orders" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorFeatureRoute feature="tableManagement"><VendorTableOrdersView /></VendorFeatureRoute></PrivateRoute>} /> {/* Table Orders with Metrics and Filtering */}
        <Route path="/vendor/kitchen" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorFeatureRoute feature="kitchenDisplay"><LightPinned><KitchenDisplayPage /></LightPinned></VendorFeatureRoute></PrivateRoute>} /> {/* Kitchen Display with KOT printing */}
        <Route path="/vendor/manage-layout" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorFeatureRoute feature="tableManagement"><ManageLayoutPage /></VendorFeatureRoute></PrivateRoute>} /> {/* Manage Floors/Sections/Tables */}
        <Route path="/vendor/settings" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorSettings /></PrivateRoute>} />
        <Route path="/vendor/steward-settings" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorStewardSettings /></PrivateRoute>} />
        <Route path="/vendor/steward-reports" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorStewardReportPage /></PrivateRoute>} />
        <Route path="/vendor/socket-diagnostics" element={<PrivateRoute roles={["ROLE_VENDOR"]}><SocketDiagnostics /></PrivateRoute>} />
        <Route path="/vendor/configuration" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorConfigurationPage /></PrivateRoute>} />
        <Route path="/vendor/tax-config" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorTaxConfiguration /></PrivateRoute>} />
        {/* Deliberately NOT behind the loyaltyPrograms feature gate any more.
            That gate exists because the OLD loyalty model is rupee-calibrated —
            loyalty_programs.points_per_rupee, which is why CountryConfig.GB sets
            loyaltySupported=false. This page is built on `promotions`, whose
            equivalent column is points_per_UNIT: a pound and a rupee are both one
            unit, so nothing needs disabling. Keeping the gate would lock GB
            vendors out of happy hours and coupons for a reason that never
            applied to them. */}
        <Route path="/vendor/loyalty" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorLoyaltyPrograms /></PrivateRoute>} />
        {/* Offers used to live at /vendor/offers on their own page. They are now
            part of Loyalty Programs — one page for every kind of offer — so the
            old path redirects rather than 404ing for anyone holding a bookmark. */}
        <Route path="/vendor/offers" element={<Navigate to="/vendor/loyalty" replace />} />
        <Route path="/vendor/tips" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorFeatureRoute feature="tipsDistribution"><VendorTips /></VendorFeatureRoute></PrivateRoute>} />
        <Route path="/vendor/printers" element={<PrivateRoute roles={["ROLE_VENDOR"]}><PrinterManagement /></PrivateRoute>} />
        <Route path="/vendor/kitchens" element={<PrivateRoute roles={["ROLE_VENDOR"]}><KitchenManagement /></PrivateRoute>} />
        <Route path="/vendor/visual-layout-2d" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorFeatureRoute feature="tableManagement"><Visual2DLayoutManager /></VendorFeatureRoute></PrivateRoute>} />
        <Route path="/vendor/kitchen-map" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorFeatureRoute feature="kitchenDisplay"><LightPinned><VisualKitchenMapPage /></LightPinned></VendorFeatureRoute></PrivateRoute>} />
        <Route path="/vendor/kot-display" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorFeatureRoute feature="kitchenDisplay"><LightPinned><KOTDisplay /></LightPinned></VendorFeatureRoute></PrivateRoute>} />
        <Route path="/vendor/kot-display/:kitchenId" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorFeatureRoute feature="kitchenDisplay"><LightPinned><KOTDisplay /></LightPinned></VendorFeatureRoute></PrivateRoute>} />
        <Route path="/vendor/order-status-display" element={<PrivateRoute roles={["ROLE_VENDOR"]}><LightPinned><OrderStatusDisplay /></LightPinned></PrivateRoute>} />
        <Route path="/vendor/multi-menu" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorFeatureRoute feature="multipleMenus"><MultiMenuManagement /></VendorFeatureRoute></PrivateRoute>} />
        <Route path="/vendor/inventory" element={<PrivateRoute roles={["ROLE_VENDOR", "ROLE_STEWARD"]}><InventoryManagement /></PrivateRoute>} />
        <Route path="/vendor/finance" element={<PrivateRoute roles={["ROLE_VENDOR"]}><FinanceDashboard /></PrivateRoute>} />
        <Route path="/vendor/finance/dashboard" element={<PrivateRoute roles={["ROLE_VENDOR"]}><FinanceDashboard /></PrivateRoute>} />
        <Route path="/vendor/finance/expenses" element={<PrivateRoute roles={["ROLE_VENDOR"]}><ExpenseManagement /></PrivateRoute>} />
        <Route path="/vendor/finance/income" element={<PrivateRoute roles={["ROLE_VENDOR"]}><IncomeManagement /></PrivateRoute>} />
        <Route path="/vendor/finance/cash" element={<PrivateRoute roles={["ROLE_VENDOR"]}><CashRegister /></PrivateRoute>} />
        <Route path="/vendor/finance/staff" element={<PrivateRoute roles={["ROLE_VENDOR"]}><StaffPayments /></PrivateRoute>} />
        <Route path="/vendor/finance/payables" element={<PrivateRoute roles={["ROLE_VENDOR"]}><SupplierPayables /></PrivateRoute>} />
        <Route path="/vendor/finance/settings" element={<PrivateRoute roles={["ROLE_VENDOR"]}><FinanceSettings /></PrivateRoute>} />
        <Route path="/vendor/finance/import" element={<PrivateRoute roles={["ROLE_VENDOR"]}><BankImport /></PrivateRoute>} />
        <Route path="/vendor/finance/eod-summary" element={<PrivateRoute roles={["ROLE_VENDOR"]}><EodSummary /></PrivateRoute>} />
        <Route path="/vendor/finance/eod-history" element={<PrivateRoute roles={["ROLE_VENDOR"]}><EodHistory /></PrivateRoute>} />
        <Route path="/vendor/finance/aggregators" element={<PrivateRoute roles={["ROLE_VENDOR"]}><AggregatorsPage /></PrivateRoute>} />
        <Route path="/vendor/finance/aggregators/settlement/:id" element={<PrivateRoute roles={["ROLE_VENDOR"]}><SettlementDetailPage /></PrivateRoute>} />
        <Route path="/vendor/reports" element={<PrivateRoute roles={["ROLE_VENDOR"]}><VendorReports /></PrivateRoute>} />
        {/* DEADCODE: SOG Studio route — disabled until backend flag flipped. See marketing/RESUME.md */}
        {/* <Route path="/vendor/marketing-studio" element={<PrivateRoute roles={["ROLE_VENDOR"]}><MarketingStudioPage /></PrivateRoute>} /> */}
        <Route path="/vendor/reservations" element={<PrivateRoute roles={["ROLE_VENDOR"]}><ReservationDashboard /></PrivateRoute>} />
        <Route path="/vendor/reservation-settings" element={<PrivateRoute roles={["ROLE_VENDOR"]}><ReservationSettings /></PrivateRoute>} />
        <Route path="/vendor/whatsapp-simulator" element={<WhatsAppSimulator />} /> {/* Public: testing tool for WhatsApp ordering flow */}
        {/* Dashboard route deprecated for steward; redirect to kitchen display or tables if needed */}
        <Route path="/steward/notifications" element={<PrivateRoute roles={["ROLE_STEWARD"]}><StewardNotificationPage /></PrivateRoute>} />
        <Route path="/steward/tables" element={<PrivateRoute roles={["ROLE_STEWARD"]}><StewardTableManagement /></PrivateRoute>} />
        <Route path="/steward/scanner" element={<PrivateRoute roles={["ROLE_STEWARD"]}><Scanner /></PrivateRoute>} />
        <Route path="/steward/kot-display/:kitchenId" element={<PrivateRoute roles={["ROLE_STEWARD"]}><LightPinned><KOTDisplay /></LightPinned></PrivateRoute>} />
        <Route path="/steward/account" element={<PrivateRoute roles={["ROLE_STEWARD"]}><StewardAccount /></PrivateRoute>} />
        <Route path="/steward/attendance" element={<PrivateRoute roles={["ROLE_STEWARD"]}><StewardAttendancePage /></PrivateRoute>} />
        {/* Legacy /steward/finance redirect — restricted stewards now reuse
            the standard /vendor/finance/income flow with filtered nav. Keep
            the redirect so any old bookmarks still resolve. */}
        <Route path="/steward/finance" element={<FinanceOnlyStewardRoute><Navigate to="/vendor/finance/income" replace /></FinanceOnlyStewardRoute>} />
        <Route path="/admin/orders" element={<PrivateRoute roles={["ROLE_ADMIN"]}><AdminOrders /></PrivateRoute>} />
        <Route path="/admin/vendors/:vendorId/page-access" element={<PrivateRoute roles={["ROLE_ADMIN","ROLE_VENDOR"]}><VendorPageAccess /></PrivateRoute>} />
        <Route path="/admin/vendors/:vendorId/menu" element={<PrivateRoute roles={["ROLE_ADMIN","ROLE_VENDOR"]}><AdminVendorMenu /></PrivateRoute>} />
        <Route path="/admin/vendor-onboarding" element={<PrivateRoute roles={["ROLE_ADMIN"]}><AdminVendorOnboarding /></PrivateRoute>} />
        <Route path="/admin/menu-management" element={<PrivateRoute roles={["ROLE_ADMIN"]}><AdminMenuManagement /></PrivateRoute>} />
        <Route path="/admin/portion-templates" element={<PrivateRoute roles={["ROLE_ADMIN"]}><AdminPortionTemplates /></PrivateRoute>} />
        <Route path="/admin/users" element={<PrivateRoute roles={["ROLE_ADMIN"]}><UserManagement /></PrivateRoute>} />
        <Route path="/admin/stewards" element={<PrivateRoute roles={["ROLE_ADMIN", "ROLE_VENDOR"]}><StewardManagement /></PrivateRoute>} />
        <Route path="/admin/api-performance" element={<PrivateRoute roles={["ROLE_ADMIN"]}><ApiPerformanceDashboard /></PrivateRoute>} />
        <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
        <Route path="/customer/dashboard" element={<PrivateRoute><CustomerDashboard /></PrivateRoute>} />
        <Route path="/rewards" element={<PrivateRoute><CustomerRewards /></PrivateRoute>} />
        <Route path="/invoices" element={<PrivateRoute><CustomerInvoices /></PrivateRoute>} />
        <Route path="/invoices/:invoiceId" element={<PrivateRoute><InvoiceDetail /></PrivateRoute>} />
        <Route path="/customer/order/:orderId" element={<PrivateRoute><CustomerOrderDetailsPage /></PrivateRoute>} />
        <Route path="/vendors/:vendorId/invoices" element={<PrivateRoute><VendorInvoices /></PrivateRoute>} />
        {/* Edge till cash drawer + refunds — staff screen. Self-gates: renders an
            inactive message unless VITE_EDGE_MODE=true, and every backend call is
            token-gated by the local agent (X-Edge-Token). */}
        <Route path="/edge/cash" element={<EdgeCashPage />} />
        <Route path="/diagnostics" element={<PrivateRoute><DiagnosticsPage /></PrivateRoute>} />
        <Route path="/messaging-diagnostics" element={<PrivateRoute><MessagingDiagnostics /></PrivateRoute>} />
        <Route path="/" element={<RoleBasedRedirect />} />
        <Route path="*" element={<RoleBasedRedirect />} />
      </Routes>
    </Layout>
    </SessionValidator>
    </QueryClientProvider>
  );
};

export default App;
